/**
 * La liaison avec la Room : créer, rejoindre, agir, revenir.
 *
 * Tout ce qui est calculable vit ailleurs — `session-reseau.ts` transforme l'état
 * reçu en `Session`, et `tableau.ts` dit quoi afficher. Ici il ne reste que ce qui
 * ne peut pas être pur : une connexion, des effets, et un peu de rangement.
 *
 * ## Le jeton de siège
 *
 * Il arrive en message privé à l'entrée et **ne circule jamais dans l'état** : le
 * code de partie est un identifiant pratique, pas une preuve d'identité (§ 6.3). On
 * le range par code de partie, pour pouvoir réclamer son siège après une coupure.
 *
 * ⚠️ `localStorage` reste **interdit pour sauvegarder une partie** (§ 5.3) : l'OS peut
 * le purger sans avertir. Un jeton de siège, lui, n'est pas une partie — le perdre
 * coûte le droit de revenir, pas le déroulement du jeu, qui vit sur le serveur.
 */

import { Client, type Room } from "@colyseus/sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  PROTOCOLE, REGLES_MAISON, type Action, type VuePartie,
} from "@zinc421/shared";

import {
  jetJoue as avancerFile, recevoirEtat, suivreDepart, type EtatReseau,
} from "./session-reseau.js";

export type Statut = "accueil" | "connexion" | "salon" | "partie" | "erreur";

export interface JoueurSalon {
  readonly siege: number;
  readonly nom: string;
  readonly estIA: boolean;
  readonly niveauIA: number;
  readonly presence: string;
}

export interface EnLigne {
  readonly statut: Statut;
  readonly code: string | null;
  readonly monSiege: number | null;
  readonly estHote: boolean;
  readonly joueurs: readonly JoueurSalon[];
  readonly phase: string;
  /** « La cave », « La recave », « La cave des caves ». */
  readonly etape: string;
  readonly etat: EtatReseau;
  /** Dernier message d'erreur bloquant (connexion refusée, partie introuvable). */
  readonly erreur: string | null;
  /** Dernier refus d'action du serveur — utile, jamais fatal. */
  readonly refus: string | null;
  readonly creer: (nom: string) => void;
  readonly rejoindre: (code: string, nom: string) => void;
  readonly ajouterIA: (niveau: number, nom: string) => void;
  readonly demarrer: () => void;
  readonly jouer: (action: Action) => void;
  readonly jetJoue: () => void;
  readonly quitter: () => void;
}

/** Le serveur de jeu sert aussi le client en production : même origine. */
function adresseServeur(): string {
  return import.meta.env.DEV ? "http://localhost:3000" : globalThis.location.origin;
}

function cleJeton(code: string): string {
  return `zinc421-siege-${code}`;
}

function lireJeton(code: string): string | undefined {
  try {
    return globalThis.localStorage?.getItem(cleJeton(code)) ?? undefined;
  } catch {
    // Safari en navigation privée peut refuser l'accès au stockage.
    return undefined;
  }
}

function rangerJeton(code: string, jeton: string): void {
  try {
    globalThis.localStorage?.setItem(cleJeton(code), jeton);
  } catch { /* sans stockage, on ne pourra pas réclamer son siège : ça n'empêche pas de jouer */ }
}

export function usePartieEnLigne(): EnLigne {
  const [statut, setStatut] = useState<Statut>("accueil");
  const [code, setCode] = useState<string | null>(null);
  const [monSiege, setMonSiege] = useState<number | null>(null);
  const [joueurs, setJoueurs] = useState<readonly JoueurSalon[]>([]);
  const [phase, setPhase] = useState("CONFIG");
  const [etape, setEtape] = useState("");
  const [etat, setEtat] = useState<EtatReseau>(suivreDepart);
  const [erreur, setErreur] = useState<string | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  const room = useRef<Room | null>(null);
  const actions = useRef(0);
  /** Dernière révision connue : c'est elle qu'on renvoie avec chaque action. */
  const revision = useRef(0);

  const client = useMemo(() => new Client(adresseServeur()), []);

  const brancher = useCallback((salle: Room, codeSalle: string) => {
    room.current = salle;
    setCode(codeSalle);
    setErreur(null);

    salle.onMessage("bienvenue", (m: { siege: number; jeton: string }) => {
      setMonSiege(m.siege);
      if (m.jeton) rangerJeton(codeSalle, m.jeton);
    });
    salle.onMessage("refus", (m: { motif: string; texte: string; revision: number }) => {
      revision.current = m.revision;
      // Un rejeu n'est pas une anomalie : c'est le serveur qui nous protège d'une
      // double action. L'afficher inquiéterait pour rien.
      setRefus(m.motif === "DEJA_VU" ? null : m.texte);
    });
    salle.onMessage("echo", () => { /* sonde de liaison */ });

    salle.onStateChange((brut) => {
      // La Room connaît une phase de plus que le moteur : `CONFIG`, le temps du
      // salon. C'est une notion de salle, pas de règles — d'où ce type élargi
      // plutôt qu'un `Phase` étendu, qui contaminerait tout le moteur.
      const vue = JSON.parse(JSON.stringify(brut)) as Omit<VuePartie, "phase"> & {
        phase: string; joueurs: readonly JoueurSalon[]; hote: number; code: string;
        etape: string;
      };
      revision.current = vue.revision;
      setPhase(vue.phase);
      setEtape(vue.etape ?? "");
      setJoueurs(vue.joueurs);
      if (vue.code) setCode(vue.code);

      if (vue.phase === "CONFIG") {
        setStatut("salon");
        return;
      }
      setStatut("partie");
      // Le RuleSet du serveur ne diffère que par la masse, choisie au salon.
      setEtat((precedent) => recevoirEtat(
        precedent, vue as unknown as VuePartie, { ...REGLES_MAISON, masse: vue.masse }));
    });

    salle.onError((_, message) => { setErreur(message ?? "erreur de partie"); });
    salle.onLeave(() => {
      if (room.current === salle) room.current = null;
    });
  }, []);

  const creer = useCallback((nom: string) => {
    setStatut("connexion");
    void client.create("partie", { nom })
      // Le code affiché est celui de l'ÉTAT, pas le `roomId` : c'est le code court
      // que les autres taperont. Il arrive au premier patch.
      .then((salle) => { brancher(salle, salle.roomId); })
      .catch((e: unknown) => {
        setStatut("erreur");
        setErreur(e instanceof Error ? e.message : String(e));
      });
  }, [client, brancher]);

  const rejoindre = useCallback((codeVoulu: string, nom: string) => {
    // Le code court se dicte : casse et espaces n'ont pas à compter. Le serveur le
    // traduit en identifiant de Room — c'est une route HTTP et non un filtre de
    // matchmaking, pour pouvoir dire « ce code n'existe pas » avant d'ouvrir une
    // WebSocket, et le distinguer d'une partie qui refuse l'entrée.
    const propre = codeVoulu.trim().toUpperCase();
    setStatut("connexion");
    void fetch(`${adresseServeur()}/partie/${encodeURIComponent(propre)}`)
      .then((r) => (r.ok ? r.json() as Promise<{ roomId: string }> : null))
      .then((reponse) => {
        if (!reponse) {
          setStatut("erreur");
          setErreur(
            `Aucune partie « ${propre} » ouverte. Vérifie le code, `
            + "ou demande une nouvelle invitation.");
          return;
        }
        // Le jeton, s'il existe, permet de reprendre son siège dans une partie déjà
        // commencée. Sans lui, une partie en cours refuse les entrées.
        const jeton = lireJeton(propre);
        return client.joinById(reponse.roomId, jeton ? { nom, jeton } : { nom })
          .then((salle) => { brancher(salle, propre); });
      })
      .catch(() => {
        setStatut("erreur");
        setErreur("La table existe, mais elle n'accepte plus d'entrée.");
      });
  }, [client, brancher]);

  const ajouterIA = useCallback((niveau: number, nom: string) => {
    // Le nom part d'ici, dans la langue de l'hôte : le serveur ne connaît pas la
    // langue de ses joueurs, et toute la table doit lire le même nom.
    room.current?.send("ajouterIA", { niveau, nom });
  }, []);

  const demarrer = useCallback(() => { room.current?.send("demarrer", {}); }, []);

  const jouer = useCallback((action: Action) => {
    const salle = room.current;
    if (!salle) return;
    setRefus(null);
    salle.send("action", {
      protocole: PROTOCOLE,
      actionId: `${salle.sessionId}-${actions.current++}`,
      revision: revision.current,
      action,
    });
  }, []);

  const jetJoue = useCallback(() => { setEtat(avancerFile); }, []);

  const quitter = useCallback(() => {
    void room.current?.leave();
    room.current = null;
    setStatut("accueil");
    setCode(null);
    setMonSiege(null);
    setJoueurs([]);
    setEtape("");
    setEtat(suivreDepart());
    setErreur(null);
    setRefus(null);
  }, []);

  // Une partie quittée sans prévenir laisse une Room qui attend : on part proprement.
  useEffect(() => () => { void room.current?.leave(); }, []);

  const estHote = monSiege === 0;

  return {
    statut, code, monSiege, estHote, joueurs, phase, etape, etat, erreur, refus,
    creer, rejoindre, ajouterIA, demarrer, jouer, jetJoue, quitter,
  };
}
